// Utility functions for downloading documents as PDF or Word files.
// Libraries are dynamically imported so they are only loaded when needed.
import { sanitizeSafeHtml } from '../components/common/SafeHtml';

export function sanitizeFilename(title) {
    const withoutControlCharacters = Array.from(title || 'document')
        .filter((character) => character.codePointAt(0) >= 0x20)
        .join('');

    return withoutControlCharacters
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/\s+/g, '_')
        .slice(0, 100);
}

export async function downloadPdf(exportHtml, filename) {
    const html2pdf = (await import('html2pdf.js')).default;
    const safeExportHtml = sanitizeSafeHtml(exportHtml);

    await html2pdf()
        .set({
            margin: [10, 10, 10, 10],
            filename: `${filename}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 },
            jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
            // Raster page slicing otherwise cuts through text baselines. Keep
            // ordinary paragraphs/list entries/rows intact when they fit a page.
            pagebreak: { mode: ['css', 'legacy'], avoid: ['.export-section', 'p', 'li', 'tr', 'h1', 'h2', 'h3', 'h4'] },
        })
        .from(safeExportHtml, 'string')
        .save();
}

export async function downloadDocx(exportHtml, filename) {
    const safeExportHtml = sanitizeSafeHtml(exportHtml);
    const fullHtml = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8">
        <style>
            body { font-family: Arial, sans-serif; font-size: 11pt; }
            p { margin: 0 0 6pt 0; }
            h3 { margin: 12pt 0 4pt 0; font-size: 13pt; }
            table { border-collapse: collapse; width: 100%; margin: 6pt 0; }
            td, th { border: 1px solid #999; padding: 4pt 6pt; }
        </style>
        </head>
        <body>${safeExportHtml}</body>
        </html>`;

    const blob = new Blob([fullHtml], { type: 'application/vnd.ms-word' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
